
import { Gerador } from "@/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, TrendingUp, PieChart, Zap, ArrowRight, BarChart3, DollarSign, Calculator } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface AnaliseViabilidadeProps {
  geradorSelecionado: Gerador | null;
  consumoMedio: number;
  gastoEnergia: number;
  economiaEstimada: number;
  percentualEconomia: number;
  percentualEnergiaAdquirida: number;
  creditosInsuficientes: boolean;
}

const AnaliseViabilidade = ({
  geradorSelecionado,
  consumoMedio,
  gastoEnergia,
  economiaEstimada,
  percentualEconomia,
  percentualEnergiaAdquirida,
  creditosInsuficientes
}: AnaliseViabilidadeProps) => {
  if (!geradorSelecionado || consumoMedio <= 0) {
    return (
      <Card className="border-dashed border-gray-300 bg-gray-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground flex items-center">
            <Calculator className="h-4 w-4 mr-2" />
            Análise de Viabilidade
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground text-center py-6">
            <p>Selecione um gerador e informe os dados de consumo para visualizar a análise de viabilidade.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white shadow-sm">
      <CardHeader className="pb-2 border-b border-indigo-100">
        <CardTitle className="text-base font-medium text-indigo-700 flex items-center">
          <Calculator className="h-5 w-5 mr-2 text-indigo-600" />
          Análise de Viabilidade
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {creditosInsuficientes && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Atenção: Créditos insuficientes</p>
              <p className="mt-1">
                O gerador possui {geradorSelecionado.creditosDisponiveis.toFixed(0)} kWh disponíveis, 
                porém o consumo estimado é de {consumoMedio.toFixed(0)} kWh.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white p-4 rounded-md shadow-sm border border-indigo-100">
            <h3 className="text-sm font-medium text-indigo-900 mb-2 flex items-center">
              <Zap className="h-4 w-4 mr-2 text-indigo-600" />
              Consumo e Economia
            </h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Consumo Médio:</span>
                  <span className="font-medium">{consumoMedio.toFixed(2)} kWh/mês</span>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gasto Mensal:</span>
                  <span className="font-medium">R$ {gastoEnergia.toFixed(2)}/mês</span>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Economia Estimada:</span>
                  <span className="font-medium text-green-600">R$ {economiaEstimada.toFixed(2)}/mês</span>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Percentual de Economia:</span>
                  <span className="font-medium text-green-600">{percentualEconomia.toFixed(2)}%</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-md shadow-sm border border-indigo-100">
            <h3 className="text-sm font-medium text-indigo-900 mb-2 flex items-center">
              <BarChart3 className="h-4 w-4 mr-2 text-indigo-600" />
              Perfil de Energia
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center text-sm mb-1">
                  <span className="text-muted-foreground">Percentual da capacidade utilizada:</span>
                  <span className={cn(
                    "font-medium",
                    percentualEnergiaAdquirida > 90 ? "text-red-600" : "text-indigo-600"
                  )}>
                    {percentualEnergiaAdquirida.toFixed(2)}%
                  </span>
                </div>
                <Progress 
                  value={Math.min(percentualEnergiaAdquirida, 100)} 
                  className="h-2"
                  indicatorClassName={percentualEnergiaAdquirida > 90 ? "bg-red-500" : "bg-indigo-500"}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="bg-indigo-50 p-2 rounded-md">
                  <p className="text-xs text-muted-foreground">Geração Total do Gerador</p>
                  <p className="font-medium">{geradorSelecionado.geracaoEstimada.toFixed(2)} kWh/mês</p>
                </div>
                <div className="bg-indigo-50 p-2 rounded-md">
                  <p className="text-xs text-muted-foreground">Tarifa de Venda</p>
                  <p className="font-medium">R$ {geradorSelecionado.tarifaVenda.toFixed(4)}/kWh</p>
                </div>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Economia anual estimada:</span>
                <span className="font-medium text-green-600">R$ {(economiaEstimada * 12).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-indigo-100 p-4 rounded-md">
          <div className="flex items-center">
            <DollarSign className="h-5 w-5 text-indigo-600 mr-2" />
            <h3 className="text-sm font-medium text-indigo-900">Fluxo Financeiro</h3>
          </div>
          <div className="mt-3 relative overflow-hidden">
            <div className="grid grid-cols-3 gap-1 text-xs">
              <div className="bg-white p-2 rounded-md">
                <p className="text-muted-foreground">Cliente paga</p>
                <p className="font-medium text-indigo-900">R$ {(gastoEnergia - economiaEstimada).toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-center">
                <ArrowRight className="h-4 w-4 text-indigo-400" />
              </div>
              <div className="bg-white p-2 rounded-md">
                <p className="text-muted-foreground">Gerador recebe</p>
                <p className="font-medium text-indigo-900">R$ {(consumoMedio * geradorSelecionado.tarifaVenda).toFixed(2)}</p>
              </div>
            </div>
            <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-indigo-300 -translate-y-1/2 z-0"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnaliseViabilidade;
